import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Pause,
  RotateCcw,
  Search,
  Trash,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";
import { CorrectionRow, type CorrectionRowData } from "./correction-row";
import { formatCorrectionTarget } from "./correction-target";
import { ProfileHistoryDrawer } from "./profile-history-drawer";

interface AuditRow {
  id: string;
  evidenceId: string;
  targetKind: string;
  targetRef: string | null;
  /** Candidate name the feedback came from — joined from labs_evaluations on the server.
   *  Null when the source evaluation was deleted or the correction predates the column. */
  candidateName: string | null;
  /** labs_evaluations.id. Present whenever the source evaluation still exists,
   *  so the UI can deep-link back to the full assessment. */
  evaluationId: string | null;
  /** Discipline of the source evaluation (labs_evaluations.disciplineSlug).
   *  Null when the source evaluation is gone or the field wasn't captured. */
  disciplineSlug: string | null;
  originalScore: string;
  correctedScore: string;
  rationale: string;
  status: string;
  policy: string;
  correctedBy: string | null;
  correctedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  appliedAt: string | null;
  hasPatch: boolean;
}

interface AuditListResponse {
  rows: AuditRow[];
  total: number;
  limit: number;
  offset: number;
}

const AUDIT_PAGE_SIZE = 25;

/**
 * Bridge between the audit-table row shape and the shared `CorrectionRow`
 * data shape. Other surfaces (parked queue, corpus drawer) build their own
 * `CorrectionRowData` from their own row types, so the formatting helpers
 * in this file stay co-located with the audit row type.
 */
function auditRowToCorrectionRowData(row: AuditRow): CorrectionRowData {
  const target = formatCorrectionTarget(row);
  return {
    id: row.id,
    targetSection: target.section,
    targetDetail: target.detail,
    originalScore: row.originalScore,
    correctedScore: row.correctedScore,
    rationale: row.rationale,
    candidateName: row.candidateName,
    correctedAt: row.correctedAt,
  };
}

const STATUS_COPY: Record<
  string,
  { label: string; tone: "ok" | "warn" | "info" | "error" | "muted" }
> = {
  applied: { label: "aplicado", tone: "ok" },
  awaiting_review: { label: "aguardando", tone: "warn" },
  pending: { label: "em andamento", tone: "info" },
  running: { label: "rodando", tone: "info" },
  parked: { label: "parado", tone: "muted" },
  conflict: { label: "conflito", tone: "error" },
  failed: { label: "falhou", tone: "error" },
  rejected: { label: "descartado", tone: "muted" },
  reverted: { label: "revertido", tone: "muted" },
};

const TONE_STYLE: Record<string, { bg: string; color: string }> = {
  // "Aplicado" gets the only green pill in the table — it's the success state
  // and should pop visually next to the navy info, amber warn, and rose error
  // tones the other statuses share. Emerald 50 / 800 mirrors the success tones
  // already used elsewhere (e.g. corpus grade chip "ex" band).
  ok: { bg: "rgba(22,163,74,0.12)", color: "#166534" },
  warn: { bg: "#FFEDD5", color: "#9A3412" },
  info: { bg: "rgba(15,44,89,0.06)", color: "#0f2c59" },
  error: { bg: "rgba(244,54,76,0.1)", color: "#f4364c" },
  muted: { bg: "rgba(107,114,128,0.12)", color: "#4b5563" },
};

interface AuditSectionProps {
  discipline?: string;
}

/**
 * Full correction audit trail. One row per CHRO correction submitted, no
 * matter how it resolved (applied, rejected, stuck at awaiting_review,
 * failed). The rationale column is unsanitized on purpose — the CHRO
 * authored it and should see their own words.
 */
export function AuditSection({ discipline }: AuditSectionProps = {}) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState<string | null>(null);
  // Filter state. Single status chip selection + free-text candidate search.
  // Same UX shape as the archive (II · Histórico) so the two surfaces feel
  // symmetrical when the operator pivots between them. Filters live on the
  // server now so pagination composes with them.
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);
  // Reset to page 0 whenever the filter narrows. Deps are the trigger;
  // the body intentionally doesn't read them.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are the trigger, body uses none of them
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);
  const [pendingAction, setPendingAction] = useState<{
    row: AuditRow;
    kind: "revert" | "reject" | "delete";
  } | null>(null);
  /** Source evaluation we're showing in the parsed-history drawer.
   *  Null = closed. Same shape (id + candidate name) the drawer needs to
   *  fetch + render its sections. */
  const [historyEval, setHistoryEval] = useState<{
    id: string;
    candidateName: string | null;
  } | null>(null);

  const offset = page * AUDIT_PAGE_SIZE;
  const { data, isLoading } = useQuery<AuditListResponse>({
    queryKey: ["labs", "correctionsAuditList", { offset, search, statusFilter, discipline }],
    queryFn: () =>
      rpcClient.labs.correctionsAuditList({
        limit: AUDIT_PAGE_SIZE,
        offset,
        status: statusFilter !== "all" ? statusFilter : undefined,
        discipline,
      }) as Promise<AuditListResponse>,
    placeholderData: (prev) => prev,
  });

  // Stats fetch — drives the legacy-repair banner. Stays non-paginated so
  // the button counter reflects all anonymous/orphan rows the click will
  // touch, not just the current page. Capped at 500 (the controller's
  // hard limit) which is plenty for the legacy backlog this targets.
  const { data: legacyStats } = useQuery<AuditRow[]>({
    queryKey: ["labs", "correctionsAuditList", { legacyStats: true, discipline }],
    queryFn: () =>
      rpcClient.labs.correctionsAuditList({ limit: 500, legacy: true, discipline }) as Promise<
        AuditRow[]
      >,
    staleTime: 60_000,
  });

  // Side-fetch the archive list so we can also surface the count of
  // legacy evaluations whose author is "anonymous" — those don't appear
  // in the audit table (which only lists corrections), but they're
  // exactly what the second leg of the relink button rewrites.
  const { data: archive } = useQuery<{
    rows: { id: string; createdBy: string | null }[];
    total: number;
  }>({
    queryKey: ["labs", "evaluationsList", { legacyStats: true }],
    queryFn: () => rpcClient.labs.evaluationsList({ limit: 500 }),
    staleTime: 60_000,
  });

  const actOnCorrection = useMutation({
    mutationFn: async ({ id, kind }: { id: string; kind: "revert" | "reject" | "delete" }) => {
      if (kind === "delete") {
        await rpcClient.labs.correctionsAuditRemove({ id });
      } else if (kind === "revert") {
        await rpcClient.labs.correctionJobsRevert({ jobId: id });
      } else {
        await rpcClient.labs.correctionJobsReject({ jobId: id });
      }
    },
    onSuccess: (_, vars) => {
      setPendingAction(null);
      queryClient.invalidateQueries({ queryKey: ["labs", "correctionsAuditList"] });
      toast({
        title:
          vars.kind === "revert"
            ? "Correção revertida"
            : vars.kind === "delete"
              ? "Correção apagada"
              : "Correção descartada",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Falha na ação", description: err.message, variant: "destructive" });
    },
  });

  // Legacy bookkeeping repair, two operations in one click:
  //   - relinkOrphanCorrections: re-points legacy correction rows whose
  //     assessment_id was a hash of the LinkedIn URL onto the real
  //     labs_evaluations.id. Idempotent.
  //   - claimAnonymousAuthorship: rewrites legacy "anonymous" authors
  //     (the fall-through string from before resolveUserEmail) onto the
  //     current logged-in user. One-way — surfaced in a confirm dialog
  //     before the click fires so the operator knows.
  // A second click on a clean table is a no-op.
  const [confirmRelink, setConfirmRelink] = useState(false);
  const relinkOrphans = useMutation({
    mutationFn: async () => {
      return rpcClient.labs.correctionsAuditRelinkOrphans() as Promise<{
        repaired: number;
        unrecoverable: number;
        evaluationsUpdated: number;
        correctionsUpdated: number;
      }>;
    },
    onSuccess: (result) => {
      setConfirmRelink(false);
      queryClient.invalidateQueries({ queryKey: ["labs", "correctionsAuditList"] });
      queryClient.invalidateQueries({ queryKey: ["labs", "evaluationsList"] });
      const lines: string[] = [];
      if (result.repaired > 0) {
        lines.push(
          `${result.repaired} correç${result.repaired === 1 ? "ão" : "ões"} religad${result.repaired === 1 ? "a" : "as"} ao perfil de origem.`,
        );
      }
      if (result.unrecoverable > 0) {
        lines.push(`${result.unrecoverable} sem perfil de origem (não recuperáveis).`);
      }
      if (result.evaluationsUpdated > 0 || result.correctionsUpdated > 0) {
        lines.push(
          `Autoria atribuída a você em ${result.evaluationsUpdated} avaliaç${result.evaluationsUpdated === 1 ? "ão" : "ões"} + ${result.correctionsUpdated} correç${result.correctionsUpdated === 1 ? "ão" : "ões"} legad${result.correctionsUpdated === 1 ? "a" : "as"}.`,
        );
      }
      const totalChanged = result.repaired + result.evaluationsUpdated + result.correctionsUpdated;
      toast({
        title: totalChanged > 0 ? "Histórico legado reparado" : "Nenhum dado legado a reparar",
        description: lines.join(" ") || "Tabela já estava em dia — nada a fazer.",
      });
    },
    onError: (err: Error) => {
      setConfirmRelink(false);
      toast({
        title: "Falha ao reparar histórico legado",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Park-from-in-flight is one-click — no confirmation dialog, since the
  // action is reversible (the next batch drain picks the row right back up).
  const parkInFlight = useMutation({
    mutationFn: async (id: string) => {
      await rpcClient.labs.correctionJobsPark({ jobId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labs", "correctionsAuditList"] });
      toast({
        title: "Correção movida para a fila",
        description: "Será calibrada no próximo lote.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Falha ao mover para a fila",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Hooks must run unconditionally — declare derived state before the early
  // returns below or React will trip "rendered more hooks than previous render"
  // on the loading → loaded transition. Status filter happens server-side;
  // free-text search stays client-side over the current page (the operator
  // either knows the candidate's name and the page's 25 rows are enough to
  // confirm, or they paginate; pulling all 500+ corrections per keystroke
  // would burn the connection).
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length === 0) return rows;
    return rows.filter((r) => {
      const name = (r.candidateName ?? "").toLowerCase();
      const author = (r.correctedBy ?? "").toLowerCase();
      const rationale = (r.rationale ?? "").toLowerCase();
      return name.includes(q) || author.includes(q) || rationale.includes(q);
    });
  }, [rows, search]);

  // First render only — `placeholderData` keeps the prior page visible
  // during page swaps so the spinner only shows on the very first load.
  if (isLoading && !data) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (data && total === 0 && search.trim().length === 0 && statusFilter === "all") {
    return (
      <p className="py-8 text-sm text-muted-foreground">
        Ainda não há correções registradas. Quando a CHRO ajustar uma nota em uma avaliação, cada
        correção aparece aqui com seu desfecho.
      </p>
    );
  }

  // Three signals that the legacy-repair button has work to do:
  //   - orphan correction rows (the "(perfil removido)" entries),
  //   - corrections still attributed to "anonymous",
  //   - evaluations still attributed to "anonymous" (not visible in
  //     the audit table — the side-fetch above gives us this count).
  // Sum them so a single counter on the button captures every kind of
  // legacy junk the click will fix. Counts come from the legacy-stats
  // fetch (capped at 500) so they reflect the whole table, not just the
  // current page.
  const legacyCorrections = legacyStats ?? [];
  const orphanCount = legacyCorrections.filter((r) => r.candidateName === null).length;
  const anonymousCorrectionCount = legacyCorrections.filter(
    (r) => r.correctedBy === "anonymous",
  ).length;
  const anonymousEvaluationCount =
    archive?.rows?.filter((r) => r.createdBy === "anonymous").length ?? 0;
  const legacyCount = orphanCount + anonymousCorrectionCount + anonymousEvaluationCount;

  // Bucket counts per status — drives the chip rail. We display the
  // statuses that actually have rows in the legacy-stats sample, in a
  // fixed order ("aplicado" first because it's the most-clicked filter,
  // then pending review, then the in-flight + terminal states).
  const statusCounts: Record<string, number> = {};
  for (const r of legacyCorrections) statusCounts[r.status] = (statusCounts[r.status] ?? 0) + 1;
  const STATUS_ORDER = [
    "applied",
    "awaiting_review",
    "pending",
    "running",
    "parked",
    "rejected",
    "reverted",
    "failed",
    "conflict",
  ];
  const visibleStatuses = STATUS_ORDER.filter((s) => (statusCounts[s] ?? 0) > 0);
  const totalPages = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + AUDIT_PAGE_SIZE, total);

  return (
    <div className="py-4 space-y-2" data-testid="audit-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground" aria-live="polite">
          {total > 0 ? (
            <>
              <span className="font-semibold tabular-nums">{total}</span> correç
              {total === 1 ? "ão" : "ões"} registrada{total === 1 ? "" : "s"}
              {totalPages > 1
                ? ` · página ${page + 1} de ${totalPages}`
                : ", mais recente primeiro."}
            </>
          ) : (
            <>Nenhuma correção bate com este filtro.</>
          )}
        </div>
        {legacyCount > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setConfirmRelink(true)}
            disabled={relinkOrphans.isPending}
            data-testid="audit-relink-orphans"
            title="Religa correções legadas ao perfil de origem e atribui a você as linhas marcadas como anônimo (autoria não reversível)"
          >
            {relinkOrphans.isPending ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <RotateCcw className="h-3 w-3 mr-1" />
            )}
            Reparar {legacyCount} dado{legacyCount === 1 ? "" : "s"} legado
            {legacyCount === 1 ? "" : "s"}
          </Button>
        )}
      </div>

      <div className="audit-toolbar" data-testid="audit-toolbar">
        <div className="audit-search">
          <Search className="audit-search-icon" aria-hidden />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por candidato, autor ou motivo (página atual)…"
            className="audit-search-input"
            aria-label="Buscar correções"
            data-testid="audit-search"
          />
          {searchInput && (
            <button
              type="button"
              className="audit-search-clear"
              onClick={() => setSearchInput("")}
              aria-label="Limpar busca"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="audit-chips" role="tablist" aria-label="Filtrar por status">
          <button
            type="button"
            role="tab"
            aria-selected={statusFilter === "all"}
            className={`audit-chip${statusFilter === "all" ? " is-active" : ""}`}
            onClick={() => setStatusFilter("all")}
            data-testid="audit-chip-all"
          >
            <span className="audit-chip-label">Todas</span>
          </button>
          {visibleStatuses.map((s) => {
            const copy = STATUS_COPY[s] ?? { label: s, tone: "muted" };
            return (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={statusFilter === s}
                className={`audit-chip tone-${copy.tone}${statusFilter === s ? " is-active" : ""}`}
                onClick={() => setStatusFilter(s)}
                data-testid={`audit-chip-${s}`}
              >
                <span className="audit-chip-label">{copy.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border rounded-md divide-y" data-testid="audit-table">
        {total === 0 ? (
          <div className="px-3 py-6 text-sm text-muted-foreground text-center">
            Nenhuma correção bate com este filtro.{" "}
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => {
                setSearchInput("");
                setSearch("");
                setStatusFilter("all");
              }}
            >
              limpe os filtros
            </button>
            .
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-6 text-sm text-muted-foreground text-center">
            Nenhuma linha desta página bate com a busca. Limpe a busca ou navegue para outra página.
          </div>
        ) : null}
        {filtered.map((r) => {
          const copy = STATUS_COPY[r.status] ?? { label: r.status, tone: "muted" };
          const tone = TONE_STYLE[copy.tone];
          const isOpen = expanded === r.id;
          const target = formatCorrectionTarget(r);
          const rowData = auditRowToCorrectionRowData(r);
          // Move the rationale text into aria-describedby so the row's
          // accessible name stays a short summary instead of swallowing
          // the operator's full free-text rationale.
          const rationaleId = `audit-rationale-desc-${r.id}`;
          return (
            <div key={r.id} className="audit-row-wrap group" data-testid={`audit-row-${r.id}`}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-accent/40 transition-colors"
                onClick={() => setExpanded(isOpen ? null : r.id)}
                aria-describedby={rationaleId}
                aria-expanded={isOpen}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ background: tone.bg, color: tone.color }}
                  >
                    {copy.label}
                  </span>
                </div>
                <CorrectionRow row={rowData} rationaleId={rationaleId} />
              </button>
              <div className="audit-row-quick-actions" data-testid={`audit-quick-actions-${r.id}`}>
                {r.status === "applied" && (
                  <button
                    type="button"
                    className="audit-quick-act"
                    onClick={() => setPendingAction({ row: r, kind: "revert" })}
                    aria-label={`Reverter correção sobre ${r.candidateName ?? "perfil"}`}
                    title="Reverter correção"
                    data-testid={`audit-quick-revert-${r.id}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
                {(r.status === "awaiting_review" ||
                  r.status === "pending" ||
                  r.status === "parked") && (
                  <button
                    type="button"
                    className="audit-quick-act audit-quick-danger"
                    onClick={() => setPendingAction({ row: r, kind: "reject" })}
                    aria-label={`Descartar correção sobre ${r.candidateName ?? "perfil"}`}
                    title="Descartar correção"
                    data-testid={`audit-quick-reject-${r.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
              </div>
              {isOpen && (
                <div className="px-3 py-3 bg-muted/20 text-xs space-y-2">
                  <div>
                    <span className="font-semibold">Motivo: </span>
                    {r.rationale || "(não informado)"}
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums">
                    <div>
                      <dt className="inline text-muted-foreground">Seção: </dt>
                      <dd className="inline">
                        {target.section} · {target.detail}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline text-muted-foreground">Perfil: </dt>
                      <dd className="inline">
                        {r.candidateName ?? <span className="italic">(perfil removido)</span>}
                        {r.evaluationId && (
                          <button
                            type="button"
                            className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
                            data-testid={`audit-open-evaluation-${r.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (r.evaluationId) {
                                setHistoryEval({
                                  id: r.evaluationId,
                                  candidateName: r.candidateName,
                                });
                              }
                            }}
                          >
                            <FileText className="h-3 w-3" />
                            ver avaliação
                          </button>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline text-muted-foreground">Política: </dt>
                      <dd className="inline">{r.policy}</dd>
                    </div>
                    <div>
                      <dt className="inline text-muted-foreground">Autor: </dt>
                      <dd className="inline">{r.correctedBy ?? "anônimo"}</dd>
                    </div>
                    {r.reviewedAt && (
                      <div>
                        <dt className="inline text-muted-foreground">Revisado: </dt>
                        <dd className="inline">
                          {new Date(r.reviewedAt).toLocaleString("pt-BR")} por {r.reviewedBy ?? "?"}
                        </dd>
                      </div>
                    )}
                    {r.appliedAt && (
                      <div>
                        <dt className="inline text-muted-foreground">Aplicado: </dt>
                        <dd className="inline">{new Date(r.appliedAt).toLocaleString("pt-BR")}</dd>
                      </div>
                    )}
                  </dl>
                  <div className="flex items-center gap-2 pt-1">
                    {r.status === "applied" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setPendingAction({ row: r, kind: "revert" })}
                        data-testid={`audit-revert-${r.id}`}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Reverter correção
                      </Button>
                    )}
                    {(r.status === "pending" || r.status === "running") && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => parkInFlight.mutate(r.id)}
                        disabled={parkInFlight.isPending}
                        data-testid={`audit-park-${r.id}`}
                        title="Mover para a fila parada — será calibrada no próximo lote"
                      >
                        <Pause className="h-3 w-3 mr-1" />
                        Mover para fila
                      </Button>
                    )}
                    {(r.status === "awaiting_review" ||
                      r.status === "pending" ||
                      r.status === "parked") && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs hover:text-rose-600"
                        onClick={() => setPendingAction({ row: r, kind: "reject" })}
                        data-testid={`audit-reject-${r.id}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Descartar correção
                      </Button>
                    )}
                    {r.status !== "applied" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs hover:text-rose-600"
                        onClick={() => setPendingAction({ row: r, kind: "delete" })}
                        data-testid={`audit-delete-${r.id}`}
                      >
                        <Trash className="h-3 w-3 mr-1" />
                        Apagar feedback
                      </Button>
                    )}
                  </div>
                  <div className="text-muted-foreground text-[11px]">
                    ID: <code className="font-mono">{r.id}</code>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <nav
          className="audit-pagination"
          aria-label="Paginação do histórico"
          data-testid="audit-pagination"
        >
          <button
            type="button"
            className="audit-page-btn"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label="Página anterior"
            data-testid="audit-page-prev"
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          </button>
          <span className="audit-page-label" aria-live="polite">
            {pageStart}–{pageEnd} de {total}
          </span>
          <button
            type="button"
            className="audit-page-btn"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            aria-label="Próxima página"
            data-testid="audit-page-next"
          >
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        </nav>
      )}

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(v) => {
          if (!v && !actOnCorrection.isPending) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.kind === "revert"
                ? "Reverter esta correção?"
                : pendingAction?.kind === "delete"
                  ? "Apagar este feedback?"
                  : "Descartar esta correção?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.kind === "revert"
                ? "A correção aplicada anteriormente volta para o estado original. Avaliações futuras deixam de incorporá-la."
                : pendingAction?.kind === "delete"
                  ? "O feedback some do histórico permanentemente. Use essa opção só durante a revisão antes de rodar a calibração — depois de aplicada, não é possível apagar (use reverter)."
                  : "A correção sai da fila de calibração. Nenhum ajuste automático será aplicado — você pode registrar uma nova correção a qualquer momento."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actOnCorrection.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                pendingAction &&
                actOnCorrection.mutate({ id: pendingAction.row.id, kind: pendingAction.kind })
              }
              disabled={actOnCorrection.isPending}
              className={
                pendingAction?.kind === "revert"
                  ? undefined
                  : "bg-rose-600 hover:bg-rose-700 text-white"
              }
            >
              {actOnCorrection.isPending
                ? "Processando…"
                : pendingAction?.kind === "revert"
                  ? "Reverter"
                  : pendingAction?.kind === "delete"
                    ? "Apagar"
                    : "Descartar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmRelink}
        onOpenChange={(v) => {
          if (!v && !relinkOrphans.isPending) setConfirmRelink(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reparar histórico legado?</AlertDialogTitle>
            <AlertDialogDescription>
              Duas operações em uma só: <strong>(1)</strong> as correções marcadas como{" "}
              <em>(perfil removido)</em> são religadas à avaliação que as gerou, quando
              recuperáveis; <strong>(2)</strong> linhas legadas com autor <em>anônimo</em>{" "}
              (avaliações + correções de antes do conserto da ponte de autenticação) passam a ser
              atribuídas a você. A atribuição de autoria <strong>não é reversível</strong> — depois
              de atribuída, não dá pra distinguir quais linhas tinham origem anônima. Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={relinkOrphans.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => relinkOrphans.mutate()}
              disabled={relinkOrphans.isPending}
              data-testid="audit-relink-confirm"
            >
              {relinkOrphans.isPending ? "Reparando…" : "Reparar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ProfileHistoryDrawer
        open={historyEval !== null}
        evaluationId={historyEval?.id ?? null}
        candidateName={historyEval?.candidateName ?? null}
        onClose={() => setHistoryEval(null)}
      />
    </div>
  );
}
